import React, { useState, useEffect } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { MapPin, ImageOff, TrendingUp } from 'lucide-react';
import { cities, neighborhoods } from '../data';

interface NeighborhoodMapProps {
  neighborhood: string;
  city: string;
  onMapUrlChange?: (url: string | null) => void;
  onDealsCountChange?: (count: number | null) => void;
}

export default function NeighborhoodMap({ neighborhood, city, onMapUrlChange, onDealsCountChange }: NeighborhoodMapProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [dealsCount, setDealsCount] = useState<number | null>(null);

  const normalizeFileName = (name: string): string => {
    return name
      .replace(/"/g, '')
      .replace(/'/g, '')
      .replace(/:/g, '')
      .replace(/\//g, '-')
      .replace(/\\/g, '-')
      .trim();
  };

  // Convert value (English) back to label (Hebrew) for Storage paths
  const getCityLabel = (cityValue: string): string => {
    const cityObj = cities.find(c => c.value === cityValue || c.label === cityValue);
    return cityObj ? cityObj.label : cityValue;
  };

  const getNeighborhoodLabel = (neighborhoodValue: string, cityValue: string): string => {
    const cityNeighborhoods = neighborhoods[cityValue] || [];
    const neighborhoodObj = cityNeighborhoods.find(n => n.value === neighborhoodValue || n.label === neighborhoodValue);
    return neighborhoodObj ? neighborhoodObj.label : neighborhoodValue;
  };

  useEffect(() => {
    const loadNeighborhoodData = async () => {
      if (!neighborhood || !city) {
        setImageUrl(null);
        setHasError(false);
        setDealsCount(null);
        onMapUrlChange?.(null);
        onDealsCountChange?.(null);
        return;
      }

      setIsLoading(true);
      setHasError(false);

      try {
        // Convert values (English) back to labels (Hebrew) for Storage paths
        const cityLabel = getCityLabel(city);
        const neighborhoodLabel = getNeighborhoodLabel(neighborhood, city);
        const normalizedNeighborhood = normalizeFileName(neighborhoodLabel);

        // Load map image using Hebrew names
        const imagePath = `מפות שכונות ${cityLabel}/שכונת ${normalizedNeighborhood} ${cityLabel}.png`;
        console.log('🔍 מנסה לטעון מפה:', imagePath);
        const imageRef = ref(storage, imagePath);
        const url = await getDownloadURL(imageRef);
        console.log('✅ מפה נטענה בהצלחה:', url);
        setImageUrl(url);
        onMapUrlChange?.(url);

        // Load deals count from Firestore
        const docId = `שכונת ${normalizedNeighborhood} ${cityLabel}`;
        const docRef = doc(db, 'neighborhoodDeals', docId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const count = data.deals || 0;
          setDealsCount(count);
          onDealsCountChange?.(count);
        } else {
          setDealsCount(null);
          onDealsCountChange?.(null);
        }
      } catch (error: any) {
        console.error('❌ שגיאה בטעינת מפה:', error);
        console.error('קוד שגיאה:', error.code);
        console.error('הודעת שגיאה:', error.message);
        console.log(`No map found for neighborhood: ${neighborhood} in city: ${city}`);
        setHasError(true);
        setImageUrl(null);
        onMapUrlChange?.(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadNeighborhoodData();
  }, [neighborhood, city, onMapUrlChange, onDealsCountChange]);

  if (!neighborhood) {
    return (
      <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-center h-48">
        <div className="text-center text-gray-400">
          <MapPin size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">בחר שכונה לצפייה בגבולות השכונה</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-center h-48">
        <div className="text-center text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-2"></div>
          <p className="text-sm">טוען מפת שכונה...</p>
        </div>
      </div>
    );
  }

  if (hasError || !imageUrl) {
    return (
      <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-center h-48">
        <div className="text-center text-gray-400">
          <ImageOff size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">מפה לא זמינה עבור שכונת {neighborhood}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-700 rounded-lg p-2">
      <div className="text-center mb-2">
        <h3 className="text-sm font-medium text-gray-300 flex items-center justify-center gap-1">
          <MapPin size={16} className="text-blue-400" />
          גבולות שכונת {neighborhood}
        </h3>
        {dealsCount !== null && (
          <div className="mt-2 inline-flex items-center gap-1.5 bg-green-600 bg-opacity-20 border border-green-500 rounded-full px-3 py-1">
            <TrendingUp size={14} className="text-green-400" />
            <span className="text-sm font-semibold text-green-400">{dealsCount} נכסים למכירה בשכונה</span>
          </div>
        )}
      </div>
      <div className="relative">
        <img
          src={imageUrl}
          alt={`מפת שכונת ${neighborhood}`}
          className="w-full h-auto rounded-md shadow-lg"
          style={{ maxHeight: '300px', objectFit: 'contain' }}
        />
      </div>
    </div>
  );
}